import { createFileRoute, Link } from '@tanstack/react-router'
import { BookOpen, ChevronRight, Play } from 'lucide-react'

import {
    KnowledgeBaseItem,
    knowledgeBaseItems,
} from '../../components/knowledge-base/knowledgeBaseData'
import { Footer, Header } from '../../components/landing'

const CardContent = ({ item }: { item: KnowledgeBaseItem }) => (
    <>
        <div className="relative h-72 overflow-hidden">
            <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url("${item.imageUrl}")` }}
            />
            <div
                className={`absolute left-4 top-4 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-sm ${
                    item.type === 'video'
                        ? 'bg-brand-yellow text-brand-dark'
                        : 'bg-primary text-white'
                }`}
            >
                {item.type === 'video' ? (
                    <Play className="h-3 w-3" />
                ) : (
                    <BookOpen className="h-3 w-3" />
                )}
                {item.type === 'video' ? 'Videó' : 'Cikk'}
            </div>
        </div>
        <div className="flex flex-1 flex-col p-8">
            <h3 className="mb-4 text-xl font-bold text-brand-dark">{item.title}</h3>
            <p className="mb-8 text-sm leading-relaxed text-brand-gray">{item.summary}</p>
            <div className="mt-auto flex w-full items-center justify-center gap-2 rounded-full border-2 border-primary py-3.5 font-bold text-primary transition-all group-hover:bg-primary group-hover:text-white">
                Tovább olvasom
                <ChevronRight className="h-4 w-4" />
            </div>
        </div>
    </>
)

const cardClassName =
    'group flex flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl'

const KnowledgeBaseCard = ({ item }: { item: KnowledgeBaseItem }) => {
    if (item.link) {
        return (
            <a href={item.link} target="_blank" rel="noopener noreferrer" className={cardClassName}>
                <CardContent item={item} />
            </a>
        )
    }

    return (
        <Link
            to="/knowledge-base/$articleId"
            params={{ articleId: item.id }}
            className={cardClassName}
        >
            <CardContent item={item} />
        </Link>
    )
}

const KnowledgeBasePage = () => {
    return (
        <div className="min-h-screen bg-off-white font-sans text-brand-dark">
            <Header />
            <main className="flex flex-col">
                <section className="bg-white py-24">
                    <div className="mx-auto w-full max-w-[1280px] px-6">
                        <div className="mb-16">
                            <h1 className="mb-4 text-3xl font-bold text-brand-dark md:text-4xl">
                                Tudásbázis
                            </h1>
                            <p className="max-w-xl text-brand-gray">
                                Cikkek és videók, amelyek segítenek az egészséges életmód felé
                                vezető utadon.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
                            {knowledgeBaseItems.map((item) => (
                                <KnowledgeBaseCard key={item.id} item={item} />
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/knowledge-base/')({
    head: () => ({
        meta: [{ title: 'Long-Y - Tudástár' }],
    }),
    component: KnowledgeBasePage,
    loader: async ({ context }) => {
        await context.apiUtils.webContent.getAllPublic.ensureData()
    },
})
